import React from 'react'
import LinksSection, {
  HeroLinksType
} from 'components/HeroSection/LinksSection'
import { LocationMarkerIcon } from '@heroicons/react/solid'
import Button from 'components/ui/Button'
import { LotQuery } from 'components/pages/LotBasePage/types/lotQuery'
import useInfoDialog from 'hooks/useInfoDialog'
import cn from 'classnames'
import useUser from 'queries/useUser'
import ButtonLink from 'components/ui/ButtonLink'
import PreviousPageLink from 'components/PreviousPageLink'
import { useRouter } from 'next/router'
import { logEvent } from 'utils/analytics'

import { useLotActionSubscription } from './types/LotActiveSubscription'
import { useLotSubscribe } from './types/lotSubscribe'

type Props = {
  name: string
  address?: string | null
  subscriptionLotId: number | null
  creditForSubscriptions?: NonNullable<
    LotQuery['lot']
  >['creditForSubscriptions']
  heroLinks: HeroLinksType
}

const LotSection = ({
  name,
  address = null,
  subscriptionLotId,
  creditForSubscriptions,
  heroLinks
}: Props) => {
  const { user, loading: userLoading } = useUser({
    skip: typeof window === 'undefined'
  })
  const router = useRouter()
  const infoDialog = useInfoDialog()
  const [userSubscribeToLot] = useLotSubscribe({})
  const { data: activeSubscriptionData } = useLotActionSubscription({
    variables: {
      lotId: subscriptionLotId!
    },
    skip: !subscriptionLotId || typeof window === 'undefined'
  })

  const subscribeToLot = async () => {
    await userSubscribeToLot({
      confirm: {
        title: 'Send me menus',
        message:
          'Get email notifications with food truck menus when they come to this lot'
      },
      variables: {
        lotId: subscriptionLotId!
      },
      onCompleted: () => {
        infoDialog({
          title: 'All Set!',
          content: (
            <p className="prose">
              Now you are going to get notifications into your inbox.
              <br />
              You can unsubscribe from your profile page
            </p>
          ),
          variant: 'success'
        })
        logEvent('join_group', {
          group_id: name
        })
      },
      onError: () => {
        infoDialog({
          title: 'Oops! Something is wrong!',
          content: "We've been notified.",
          variant: 'error'
        })
      }
    })
  }

  const showSubscriptionButton =
    activeSubscriptionData?.lot && !activeSubscriptionData.lot?.subscribed

  const giveCredits =
    creditForSubscriptions &&
    creditForSubscriptions?.cents > 0 &&
    !user &&
    !userLoading &&
    typeof window !== 'undefined'

  const hideMarketing =
    router.asPath.includes('boeing') || router.asPath.includes('homedepot')
  return (
    <div>
      <PreviousPageLink />
      <div className="z-100 mb-4 lg:mb-0 flex flex-row flex-nowrap items-start justify-start">
        <h1 className="sm:text-5xl text-4xl text-white font-extrabold mr-4 line-clamp-2 pb-1">
          {name}
        </h1>
      </div>

      {!hideMarketing && address && (
        <div className="mt-2 mb-5 italic text-white flex items-center">
          <div className="inline mr-2">
            <LocationMarkerIcon className="w-6 h-6 text-orange-500" />
          </div>
          {address}
        </div>
      )}

      <div className="sm:mt-6 flex hidden sm:block">
        <LinksSection socialLinks={heroLinks} />
      </div>

      {subscriptionLotId && (
        <div
          className={cn('w-72 lg:w-auto sm:mt-6', {
            invisible: !showSubscriptionButton
          })}
        >
          <div className="pr-2 pt-2 w-full md:w-1/2 lg:w-auto flex-grow">
            {user ? (
              <Button
                color="primary"
                onClick={subscribeToLot}
                variant="contained"
                shadow
              >
                Subscribe to schedule
              </Button>
            ) : (
              <ButtonLink
                color="primary"
                href={`/customers/login?subscribe_to_lot_id=${encodeURIComponent(
                  subscriptionLotId
                )}`}
                prefetch={false}
                variant="contained"
                shadow
              >
                <div>
                  <div>Subscribe to schedule {giveCredits && ' and'}</div>
                  {giveCredits && (
                    <div className="block">
                      get {creditForSubscriptions!.formatted} off your first
                      online order
                    </div>
                  )}
                </div>
              </ButtonLink>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default LotSection
